import React from 'react';
import Image from 'next/image';
import { FormattedMessage, useIntl } from 'react-intl';
import { Box, Flex } from '@osmosis/tailwind-ui/components/Box';
import { H1, P } from '@osmosis/tailwind-ui/components/Text';
import { text0, text1, bgPrimaryText } from '@osmosis/tailwind-ui/styles';
import { cn } from '@osmosis/tailwind-ui/helpers/classes';
import { useContentSize } from '@osmosis/nextjs-components/context';
import { LoggedOutHeaderData } from '../../types/library';
import { LibraryLoggedOutPrime } from './LibraryLoggedOutPrime';
import OsmosisAIButton from '../ai/OsmosisAIButton';

type Props = {
  headerData: LoggedOutHeaderData;
};

/**
 * LibraryHeaderLoggedOut.
 */
const LibraryHeaderLoggedOut = (props: Props) => {
  const { headerData } = props;
  const { title, image } = headerData;
  const { formatMessage } = useIntl();

  const { getClassValue } = useContentSize();

  const infoWidthResponsive = getClassValue({
    defaultClasses: 'w-[90%] lg:w-[62%]',
    breakpoints: { lg: 'w-[62%]', base: 'w-[90%]' },
  });

  const imageResponsive = getClassValue({
    defaultClasses: 'hidden lg:flex',
    breakpoints: { lg: 'flex', base: 'hidden' },
  });

  // remove | Osmosis for now, might put it back later
  const partialTitle = title.replace(/\s*\|\s*Osmosis\s*$/, '');

  return (
    <Flex
      data-testid="library-banner-loggedout"
      className={cn('w-full p-4 pb-8', bgPrimaryText)}
    >
      <Flex
        data-testid="library-banner-wrapper"
        className="m-auto w-full max-w-library-banner justify-between"
      >
        <Flex
          data-testid="library-banner-info"
          className={cn('flex-col justify-between', infoWidthResponsive)}
        >
          <H1 data-testid="library-banner-title" className={cn('mb-4', text0)}>
            {partialTitle}
          </H1>

          <P
            data-testid="library-banner-description"
            className={cn('mb-4 max-w-library-banner-text', text0)}
          >
            <FormattedMessage
              id="library.heading.loggedout.descr"
              defaultMessage="Osmosis turns even the toughest topics into clear, illustrated videos so you can learn rather than memorize."
            />
          </P>

          <Flex
            data-testid="library-banner-button-container"
            className="w-auto flex-wrap items-center"
          >
            <Box className="mb-2 mr-4 flex flex-col items-center sm:mb-0">
              <LibraryLoggedOutPrime
                className={cn('h-library-cta w-library-cta md:w-44')}
                data-testid="library-banner-link"
              >
                <FormattedMessage
                  id="library.heading.tryfree"
                  defaultMessage="Try Osmosis free"
                />
              </LibraryLoggedOutPrime>
              <span
                data-testid="subtext"
                className={cn(text1, 'block text-base text-center')}
              >
                <FormattedMessage
                  id="learn.lockout_modal.credit_card_required"
                  defaultMessage="No credit card required"
                />
              </span>
            </Box>

            <OsmosisAIButton
              source="Library"
              showLoggedOut
              classNames={{
                button: 'border-none rounded-full mb-8',
              }}
            />
          </Flex>
        </Flex>

        {image && (
          <Box className={cn('flex-col justify-center px-4', imageResponsive)}>
            <Image
              data-testid="library-banner-image"
              alt={formatMessage({
                id: 'library.heading.image.alt',
                defaultMessage: 'Book',
              })}
              width={200}
              height={150}
              src={image}
            />
          </Box>
        )}
      </Flex>
    </Flex>
  );
};

export default LibraryHeaderLoggedOut;
